/**
 * Shared WIP/dashboard formatting utilities (es-ES locale, € currency).
 * These are extracted from Wip.tsx inline helpers so both Paid Media and
 * Buy Media tables can share them without duplication.
 */

/** Integer with thousand separator: 1234567 → "1.234.567" */
export const fmtInt = (n: number): string =>
  Number.isFinite(n) ? Math.round(n).toLocaleString("es-ES") : "—";

/**
 * Currency with fixed decimal places (default 0): 1234.5 → "1.235 €".
 * Pass `decimals` for cent precision: fmtMoney(0.47, 2) → "0,47 €".
 * Use for totals and investment amounts.
 * null/undefined (e.g. backend IFERROR → "-") render as "—", never 0.
 */
export const fmtMoney = (n: number | null | undefined, decimals = 0): string =>
  n != null && Number.isFinite(n)
    ? n.toLocaleString("es-ES", {
        style: "currency",
        currency: "EUR",
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      })
    : "—";

/**
 * Currency with configurable decimal places: 1.2345 → "1,2345 €"
 * Use for unit costs and CPM values where precision matters (default 4 decimals).
 */
export const fmtMoneyDetailed = (n: number, decimals = 4): string =>
  Number.isFinite(n)
    ? n.toLocaleString("es-ES", {
        style: "currency",
        currency: "EUR",
        minimumFractionDigits: 2,
        maximumFractionDigits: decimals,
      })
    : "—";

/** ISO date string to localised date: "2026-05-01T…" → "1/5/2026" */
export const fmtDate = (iso: string | null): string =>
  iso ? new Date(iso).toLocaleDateString("es-ES") : "—";

/** ISO date → "01/05/26" (es-ES, 2 dígitos), o null si no hay fecha */
export const fmtDateShort = (iso?: string | null): string | null =>
  iso
    ? new Date(iso).toLocaleDateString("es-ES", {
        day: "2-digit",
        month: "2-digit",
        year: "2-digit",
      })
    : null;

/** Percentage with configurable decimal places. Value must already be ×100.
 *  e.g. fmtPct(87.3) → "87.3%", fmtPct(12.345, 2) → "12.35%" */
export const fmtPct = (n: number, decimals = 1): string =>
  Number.isFinite(n) ? `${n.toFixed(decimals)}%` : "—";

/** Fixed-decimal number string: 1.23456 → "1.23". null/undefined → "—". */
export const fmtNum = (n: number | null | undefined, decimals = 2): string =>
  n != null && Number.isFinite(n) ? n.toFixed(decimals) : "—";

/**
 * A «;»-separated CSV cell as Spanish Excel reads it: plain numbers get a
 * decimal comma (1400.43 → "1400,43") so Excel takes them as numbers, and the
 * value is quoted only when it holds the separator, a quote or a line break.
 */
export const spreadsheetCell = (v: string | number): string => {
  const s =
    typeof v === "number" ? (Number.isFinite(v) ? String(v) : "") : String(v);
  const out = /^-?\d+(\.\d+)?$/.test(s) ? s.replace(".", ",") : s;
  return /[;"\r\n]/.test(out) ? `"${out.replace(/"/g, '""')}"` : out;
};
